import {useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getRichResidentObject, selectRichResidentObjectsFromAppState} from '../../Utils/residentUtils'

// this helper hook is used to return a structured resident object (RichResidentObject) for a given resident ID
// It has 2 use cases:
//  - getResident (most common), ensures you always get a valid RichResidentObject back even if the resident ID is invalid
//  - getResidentOrNull returns null if the resident ID is invalid

// the common use case is useful in cases where you want to avoid lots of null checks in your code, for example:
// when displaying the residents table while switching buildings (resident data will disappear while new building data is loading)

/**
 * @return {{getResident: (function(string): RichResidentObject), getResidentOrNull: (function(*): RichResidentObject|null)}}
 */
function useResidentLookup() {
  /** @typedef {Array<RichResidentObject>} richResidentsArr */
  const richResidentsArr = useSelector(state => selectRichResidentObjectsFromAppState(state))
  const richResidentsLookup = useMemo(
    () =>
      richResidentsArr.reduce((acc, resident) => {
        acc[resident.id] = resident
        return acc
      }, {}),
    [richResidentsArr],
  )

  /**
   * @param {string} residentId
   * @return {RichResidentObject}
   */
  const getResident = useCallback(residentId => {
    // we can pass empty objects here because we already know the resident doesn't exist
    return richResidentsLookup[residentId] || getRichResidentObject({}, {}, {}, residentId)
  }, [richResidentsLookup])

  /**
   * @param residentId
   * @return {RichResidentObject|null}
   */
  const getResidentOrNull = useCallback(residentId => {
    return richResidentsLookup[residentId] || null
  }, [richResidentsLookup])

  return {
    getResident,
    getResidentOrNull,
  }
}

export default useResidentLookup
